import { useEffect, useRef, useState } from "react";
import { Board } from "../components/Board.tsx";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { DailyFriendsSheet } from "../components/DailyFriendsSheet.tsx";
import { Keyboard } from "../components/Keyboard.tsx";
import { NextWordCountdown } from "../components/NextWordCountdown.tsx";
import { ResultSheet } from "../components/ResultSheet.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { Toast } from "../components/Toast.tsx";
import { useDailyGame } from "../features/daily/useDailyGame.ts";
import { useBoardSelectionHint } from "../features/useBoardSelectionHint.ts";
import { useSession } from "../lib/session.ts";
import { formatDate } from "../lib/edition.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { messages } from "../i18n/index.ts";
import { leaveResultThroughAd } from "../lib/adGate.ts";

interface DailyScreenProps {
  onBack: () => void;
  onExitConfirmed: () => void;
  registerBackHandler: RegisterBackHandler;
}

function formatDay(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return key;
  return formatDate(new Date(Date.UTC(y, m - 1, d)), { day: "numeric", month: "long" });
}

export function DailyScreen({ onBack, onExitConfirmed, registerBackHandler }: DailyScreenProps) {
  const game = useDailyGame();
  const streak = useSession((s) => s.stats?.currentStreak ?? 0);
  const [resultDismissed, setResultDismissed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);

  const finished = game.status === "finished";
  const selectionHint = useBoardSelectionHint(finished ? null : game.pinned);

  /*
   * Bilemeyende sunucu seriyi sıfırlıyor, yani sonuç geldiğinde "kaç günlük
   * seri bitti" bilgisi artık yok. Oyun sürerken son bilinen değeri saklıyoruz.
   * Zaten bitmiş bir oyunu açan için null kalır; paylaşım metni o durumda
   * sayısız cümleye düşüyor.
   */
  const streakBefore = useRef<number | null>(null);
  useEffect(() => {
    if (game.status === "playing") streakBefore.current = streak;
  }, [game.status, streak]);

  useEffect(() => {
    if (!finished) setResultDismissed(false);
  }, [finished]);

  const typedDraft = game.cells.some((cell) => cell !== undefined);

  useEffect(
    () =>
      registerBackHandler(() => {
        if (friendsOpen) {
          setFriendsOpen(false);
          return "block";
        }
        if (confirmOpen) {
          setConfirmOpen(false);
          return "block";
        }
        if (finished && game.revealed && !resultDismissed) {
          void leaveResultThroughAd().then(() => setResultDismissed(true));
          return "block";
        }
        // Gönderilen tahminler sunucuda; kaybolacak tek şey yarım yazılmış satır.
        if (game.status === "playing" && typedDraft) {
          setConfirmOpen(true);
          return "block";
        }
        return "allow";
      }),
    [
      typedDraft,
      confirmOpen,
      finished,
      friendsOpen,
      game.revealed,
      game.status,
      registerBackHandler,
      resultDismissed,
    ],
  );

  if (game.status === "loading") {
    return (
      <div className="app">
        <ScreenHeader title={messages.daily.title} onBack={onBack} />
        <main className="grid place-items-center">
          <p className="text-sm text-muted">{messages.daily.loading}</p>
        </main>
      </div>
    );
  }

  if (game.status === "error") {
    return (
      <div className="app">
        <ScreenHeader title={messages.daily.title} onBack={onBack} />
        <main className="grid place-items-center px-6 text-center">
          <div>
            <p className="font-display text-lg font-bold">{messages.daily.loadFailed}</p>
            <p className="mt-2 text-sm text-muted">
              {messages.daily.connectionHint}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <ScreenHeader
        title={messages.daily.title}
        subtitle={messages.daily.streak(formatDay(game.dayKey), streak)}
        onBack={onBack}
      />

      <main className="relative grid min-h-0 place-items-center px-3">
        <Toast message={game.toast ?? selectionHint} />
        <Board
          rows={game.rows}
          cells={game.cells}
          pinned={game.pinned}
          onSelectCell={game.selectCell}
          shake={game.shake}
          won={game.solved}
        />
      </main>

      {finished && resultDismissed ? (
        <div className="finish-bar">
          <button type="button" className="btn-secondary" onClick={() => setResultDismissed(false)}>
            {messages.daily.showResult}
          </button>
          <button type="button" className="btn-primary" onClick={() => { void leaveResultThroughAd().then(onBack); }}>
            {messages.daily.home}
          </button>
        </div>
      ) : (
        <Keyboard
          states={game.keyboard}
          onPress={game.press}
          disabled={finished}
          busy={game.sending}
        />
      )}

      <ResultSheet
        open={finished && game.revealed && !resultDismissed && !friendsOpen}
        onShowFriends={() => setFriendsOpen(true)}
        won={game.solved}
        answer={game.answer}
        rows={game.rows}
        share={{
          mode: "daily",
          dayLabel: formatDay(game.dayKey),
          solved: game.solved,
          attempts: game.rows.length,
          streak,
          brokenStreak: streakBefore.current,
        }}
        onClose={() => { void leaveResultThroughAd().then(() => setResultDismissed(true)); }}
        onNewGame={() => { void leaveResultThroughAd().then(onExitConfirmed); }}
        newGameLabel={messages.daily.home}
        footer={<NextWordCountdown />}
      />

      <DailyFriendsSheet open={friendsOpen} onClose={() => setFriendsOpen(false)} />

      <ConfirmDialog
        open={confirmOpen}
        title={messages.daily.exitTitle}
        message={messages.daily.exitMessage}
        confirmLabel={messages.daily.exit}
        accent="amber"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onExitConfirmed();
        }}
      />
    </div>
  );
}
